import { useState, useEffect, FC, useCallback, useMemo } from "react";
import { APIProvider } from "@vis.gl/react-google-maps";
import { Alert, Modal, Spinner, Tab, Tabs } from "react-bootstrap";
import { useShipping } from "../../../context";
import { useAuth, useOrderOption, useStore } from "../../../hooks";
import { useCart } from "../../../hooks";
import { CustomerAddressModel, OrderTypeModel } from "../../../models";
import { OrderType } from "../../../enums";
import { CreateCart, UpdateCart } from "../../../services/ApiService";
import {
  capitalize,
  extractAddressDetails,
  formatTime12Hours,
  formatToDate,
  formatToTime,
  getOrderTypeTimeSlots,
} from "../../../helpers";
import { PlaceAutocomplete } from "../../PlaceAutocomplete";
import getBrowserFingerprint from "get-browser-fingerprint";
import { AsapLater } from "../../AsapLater";
import {
  ClockFill,
  HouseDoor,
  InfoCircleFill,
  Pencil,
  TruckFrontFill,
} from "react-bootstrap-icons";
import { BsTable } from "react-icons/bs";
import clsx from "clsx";
import { useLocation } from "react-router-dom";
import SavedAddressOptions from "../../SavedAddressOptions";
import { useCartPrice } from "../../../hooks";
import { RiArrowRightSLine, RiHandbagFill } from "react-icons/ri";
import { Toast } from "../../Toast";

const API_KEY = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;

type FormFields = {
  formatted_address: string;
  address_street: string;
  table_number: string;
};

type ValidationErrors = Partial<Record<keyof FormFields, string>>;

type Props = {
  show: boolean;
  handleClose: () => void;
};

const OrderOptionModal: FC<Props> = ({ show, handleClose }) => {
  const location = useLocation();

  const [isOpen, setIsOpen] = useState<boolean>(false);

  const [tableNumber, setTableNumber] = useState<string>("");
  const [selectedPlace, setSelectedPlace] =
    useState<google.maps.places.PlaceResult | null>(null);
  const [todaysTiming, setTodaysTiming] = useState<Date[]>([]);
  const [showAsapLater, setShowAsapLater] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [errors, setErrors] = useState<ValidationErrors>({});

  const { customerId, customer, isLoggedIn, isGuest, deviceId, setDeviceId } =
    useAuth();
  const {
    storeId,
    selectedStore,
    setStoreGateways,
    utm,
    orderTypes,
    defaultOrderType,
  } = useStore();
  const { cartId, setCartId, cartItems } = useCart();
  const { orderOption, setOrderOption } = useOrderOption();
  const { isLocationSelected, shippingLocation, setShippingLocation } =
    useShipping();

  // Lazy initialization so it runs only once
  const [selectedType, setSelectedType] = useState<number>(defaultOrderType);
  const [selectedDate, setSelectedDate] = useState<string>();
  const [selectedTime, setSelectedTime] = useState<string>();

  // Memoize validation function to prevent recreation on every render
  const validateDeliveryAddress = useCallback(
    (shippingDetails: any): ValidationErrors => {
      const newErrors: ValidationErrors = {};

      if (!isLocationSelected) {
        newErrors.formatted_address = "Please enter your address";
        return newErrors;
      }

      // console.log(shippingDetails);

      if (!shippingDetails?.zip_code) {
        newErrors.formatted_address = "Please enter your detailed address";
      }

      // Determine if the selected place is ONLY a locality (city-level) without street-level details
      // const addressComponents = selectedPlace?.address_components || [];
      // const hasLocality = addressComponents.some((c: any) => c.types?.includes('locality'));
      // const hasPremise = addressComponents.some((c: any) => c.types?.includes('premise'));
      // const hasStreetNumber = addressComponents.some((c: any) => c.types?.includes('street_number'));
      // const hasRoute = addressComponents.some((c: any) => c.types?.includes('route'));
      // const isLocalityOnly = hasLocality && !hasPremise && !hasStreetNumber && !hasRoute;

      // If selected location is NOT a locality (i.e., has street/premise detail), require address_street_1
      // if (!isLocalityOnly && !shippingDetails?.address_street_1) {
      // newErrors.address_street = 'Please enter your House no., apt., suite, floor, street, etc.';
      // }

      return newErrors;
    },
    [isLocationSelected, selectedPlace],
  );

  const handleSubmit = useCallback(async () => {
    let shippingDetails = { ...shippingLocation };

    if (selectedType === OrderType.DELIVERY) {
      const validationErrors = validateDeliveryAddress(shippingDetails);
      if (Object.keys(validationErrors).length > 0) {
        setErrors(validationErrors);
        return;
      }
    } else if (selectedType === OrderType.PICKUP) {
      shippingDetails = {
        formatted_address: selectedStore?.store_address,
        address_street: selectedStore?.store_street_address,
        city_name: selectedStore?.city_name,
        state_code: selectedStore?.state_code,
        latitude: selectedStore?.latitude,
        longitude: selectedStore?.longitude,
        zip_code: selectedStore?.store_pincode,
      };
      setShippingLocation(shippingDetails);
    } else if (selectedType === OrderType.DINEIN) {
      const newErrors: ValidationErrors = {};
      if (!tableNumber) {
        newErrors.table_number = "Please enter your table number";
        setErrors(newErrors);
        return;
      } else {
        setErrors(newErrors);
      }

      shippingDetails = {
        formatted_address: selectedStore?.store_address,
        address_street_1: tableNumber,
        address_street: selectedStore?.store_street_address,
        city_name: selectedStore?.city_name,
        state_code: selectedStore?.state_code,
        latitude: selectedStore?.latitude,
        longitude: selectedStore?.longitude,
        zip_code: selectedStore?.store_pincode,
      };
      setShippingLocation(shippingDetails);
    }

    let updatedSelectedDate = selectedDate;
    let updatedSelectedTime = selectedTime;

    if (!updatedSelectedDate || !updatedSelectedTime) {
      return;
    }

    // let checkDate = null;

    // if (selectedDate && selectedTime) {
    //   const [day, month, year] = selectedDate.split('/');
    //   const isoDate = `${year}-${month}-${day}`;

    //   checkDate = new Date(`${isoDate} ${selectedTime}`).getTime();
    // }

    // if (!checkDate || checkDate < Date.now()) {
    //   updatedSelectedDate = formatToDate(todaysTiming[0]);
    //   updatedSelectedTime = formatToTime(todaysTiming[0]);

    //   setSelectedDate(updatedSelectedDate);
    //   setSelectedTime(updatedSelectedTime);
    // }

    const selectedOrderType = orderTypes.find(
      (item: OrderTypeModel) => item.id === selectedType,
    );

    const updatedOrderOption = {
      ...orderOption,
      type_id: selectedType,
      order_date: updatedSelectedDate,
      order_time: updatedSelectedTime,
      tip_status: selectedOrderType?.tip_status || "1",
    };
    setOrderOption(updatedOrderOption);

    if (!cartId) {
      await createCart(updatedOrderOption, shippingDetails);
    } else if (location.pathname === "/checkout") {
      updateCart(updatedOrderOption);
    } else {
      // updateCart(updatedOrderOption);
      handleClose();
    }
  }, [
    selectedType,
    shippingLocation,
    orderOption,
    selectedDate,
    selectedTime,
    setOrderOption,
    validateDeliveryAddress,
    selectedStore,
    setShippingLocation,
    cartId,
    handleClose,
    tableNumber,
  ]);

  const createCart = useCallback(
    async (orderOption: any, shipping: any) => {
      setIsLoading(true);

      try {
        const { status, message, data } = await CreateCart({
          store_id: storeId,
          device_id: deviceId,
          customer_id: isLoggedIn ? customerId : "0",
          order_option: { ...orderOption, utm_source: utm },
          shipping,
          source: utm,
        });

        if (status) {
          setCartId(data.cart_id);
          setDeviceId(data.device_id);
          setOrderOption({
            ...orderOption,
            mov: data.order_option.mov,
            delivery_charge: data.order_option.delivery_charge,
          });
          handleClose();
        } else {
          Toast.error(message);
        }
      } catch (error) {
        Toast.error("An error occurred while creating the cart");
      } finally {
        setIsLoading(false);
      }
    },
    [storeId, deviceId, isLoggedIn, customerId, setCartId, handleClose],
  );

  const cartPrice = useCartPrice();

  const updateCart = async (updatedOrderOption: any) => {
    setIsLoading(true);

    try {
      const { status, message, data } = await UpdateCart({
        store_id: storeId,
        device_id: deviceId,
        customer_id: customerId,
        cart_id: cartId,
        cart: cartItems,
        order_option: { ...updatedOrderOption, utm_source: utm },
        shipping: shippingLocation,
        doordashObj: {
          external_delivery_id: `cart-${cartId}-${Date.now()}`,
          pickup_address: selectedStore?.store_address,
          dropoff_address: shippingLocation?.formatted_address,
          dropoff_location: {
            lat: shippingLocation?.latitude,
            lng: shippingLocation?.longitude,
          },
          dropoff_phone_number: customer ? customer.phone : "",
          order_value: cartPrice * 100,
        },
        source: utm,
      });

      if (status) {
        setStoreGateways(data.gateway);
        handleClose();
      } else {
        Toast.error(message);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // Memoize fingerprint setting function
  const setFingerprint = useCallback(async () => {
    setDeviceId(await getBrowserFingerprint());
  }, [setDeviceId]);

  useEffect(() => {
    if (!deviceId) setFingerprint();
    if (orderTypes.length) handleOrderTypeChange(defaultOrderType);
  }, [orderTypes, defaultOrderType, deviceId, setFingerprint]);

  useEffect(() => {
    if (show) setIsOpen(false);
  }, [show]);

  useEffect(() => {
    if (selectedPlace) {
      const {
        formatted_address,
        latitude,
        longitude,
        state,
        city,
        postal_code,
        address_street,
      } = extractAddressDetails(selectedPlace);

      setShippingLocation({
        id: "",
        formatted_address,
        address_street,
        latitude,
        longitude,
        state_code: state,
        city_name: city,
        zip_code: postal_code,
      });
    }
  }, [selectedPlace, setShippingLocation]);

  const handleOrderTypeChange = useCallback(
    (id: number) => {
      setShowAsapLater(false);

      const orderType = orderTypes.find(
        (item: OrderTypeModel) => item.id === id,
      );
      if (!orderType) return;

      const filteredTiming = getOrderTypeTimeSlots(orderType);

      setTodaysTiming(filteredTiming);
      if (filteredTiming.length) {
        setSelectedDate(formatToDate(filteredTiming[0]));
        setSelectedTime(formatToTime(filteredTiming[0]));
      }

      if (selectedType !== id) {
        setShippingLocation({});
      }

      setSelectedType(id);
    },
    [orderTypes, selectedType, setShippingLocation],
  );

  const handleAddressChange = (selectedAddress: CustomerAddressModel) => {
    setShippingLocation({
      id: selectedAddress.id,
      formatted_address: `${selectedAddress?.address_street}, ${selectedAddress?.city_name}, ${selectedAddress?.state_code}, ${selectedAddress?.zip_code}`,
      address_street: selectedAddress.address_street,
      latitude: selectedAddress.latitude,
      longitude: selectedAddress.longitude,
      state_code: selectedAddress.state_code,
      city_name: selectedAddress.city_name,
      zip_code: selectedAddress.zip_code,
    });
  };

  const handleHouseAddressChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setErrors((prev: ValidationErrors) => ({ ...prev, address_street: "" }));
    setShippingLocation((prev: any) => ({
      ...prev,
      address_street_1: e.target.value,
    }));
  };

  const handleTableNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let { value } = e.target;
    // value = value.replace(/[^a-zA-Z\s]/g, "");
    // value = value.replace(/[^a-zA-Z0-9\s]/g, "");
    value = value.replace(/[^0-9]/g, "");
    value = value.toUpperCase();

    setErrors((prev: ValidationErrors) => ({ ...prev, address_street: "" }));

    setTableNumber(value);
    // setShippingLocation((prev: any) => ({ ...prev, address_street_1: value }));
  };

  const handleDateChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      setSelectedDate(e.target.value);
      // setOrderOption({...orderOption, order_date: e.target.value});
    },
    [],
  );

  const handleTimeChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      setSelectedTime(e.target.value);
      // setOrderOption({...orderOption, order_time: e.target.value});
    },
    [],
  );

  const handlePlaceSelect = useCallback(
    (place: google.maps.places.PlaceResult | null) => {
      setSelectedPlace(place);
      setErrors((prev: ValidationErrors) => ({
        ...prev,
        formatted_address: "",
      }));
    },
    [],
  );

  // Memoize order type descriptions to prevent recreation
  const getOrderTypeDescription = useCallback((typeId: number): string => {
    switch (typeId) {
      case OrderType.DELIVERY:
        return "Your order will be delivered to your address.";
      case OrderType.PICKUP:
        return "You will Pickup the order yourself at restaurant.";
      case OrderType.DINEIN:
        return "You will Dine In at the restaurant.";
      default:
        return "";
    }
  }, []);

  // Memoize ASAP alert content
  const asapAlertContent = useMemo(() => {
    if (!todaysTiming.length) return null;

    const firstTimeSlot = todaysTiming[0];
    return (
      <div className="order-option-card">
        <h4 className="order-option-heading">
          <strong>
            {orderTypes.find((type) => type.id === selectedType)?.type} Time
          </strong>
        </h4>
        <Alert variant="success" className="pickup_time_card">
          <div className="pickup_time_card_inner">
            <div className="pickup_time_icon">
              <ClockFill />
            </div>
            <div className="pickup_time_content">
              <strong>
                {orderTypes.find((type) => type.id === selectedType)?.type}{" "}
                ASAP{" "}
              </strong>
              <span>Today at {formatTime12Hours(firstTimeSlot)}</span>
            </div>
            <span
              className="cursor-pointer"
              onClick={() => setShowAsapLater(!showAsapLater)}
            >
              {/* <img src={toAbsoluteUrl("images/greenvector.svg")} alt="" /> */}
              <RiArrowRightSLine className="modal-option-icon" size={30} />
            </span>
          </div>
        </Alert>
      </div>
    );
  }, [todaysTiming, orderTypes, selectedType, showAsapLater]);

  return (
    <>
      <Modal
        className="order_option_popup app-modal"
        contentClassName="overflow-visible"
        show={show}
        onHide={handleClose}
        fullscreen="sm-down"
        centered
      >
        <Modal.Header closeButton className="app-modal-header">
          <Modal.Title className="fs-5">
            How would you like to receive your order?
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="app-modal-body">
          <div className="peppy_hrd2">
            <div className="order-option-tabs">
              <Tabs
                style={{ background: "none" }}
                activeKey={selectedType}
                onSelect={(k) => handleOrderTypeChange(Number(k))}
              >
                {orderTypes.map((type: OrderTypeModel) => (
                  <Tab
                    key={type.id}
                    eventKey={type.id}
                    title={
                      <div className="d-flex align-items-center gap-2">
                        {type.id === OrderType.DELIVERY && (
                          <TruckFrontFill size={18} />
                        )}
                        {type.id === OrderType.PICKUP && (
                          <RiHandbagFill size={18} />
                        )}
                        {type.id === OrderType.DINEIN && <BsTable size={18} />}
                        {capitalize(type.type)}
                      </div>
                    }
                  >
                    <div className="order_option_popup_sec1">
                      {/* <small>{getOrderTypeDescription(type.id)}</small> */}
                      <div className="order-type-info">
                        <InfoCircleFill
                          className="order-type-info-icon"
                          size={22}
                        />

                        <small>{getOrderTypeDescription(type.id)}</small>
                      </div>
                      {todaysTiming.length ? (
                        asapAlertContent
                      ) : (
                        <Alert
                          variant="danger"
                          className="d-flex justify-content-between align-items-center"
                        >
                          <span>
                            Restaurant is currently not accepting orders
                          </span>
                          <Pencil
                            className="cursor-pointer "
                            onClick={() => setShowAsapLater(!showAsapLater)}
                          />
                        </Alert>
                      )}
                      {showAsapLater && (
                        <AsapLater
                          orderType={type}
                          handleDateChange={handleDateChange}
                          handleTimeChange={handleTimeChange}
                        />
                      )}
                    </div>
                  </Tab>
                ))}
              </Tabs>
            </div>
          </div>

          {selectedType === OrderType.DELIVERY && (
            <div className="order_option_popup_sec2">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <h3 className="p-0">Delivery Address</h3>
                {!isOpen && isLoggedIn && !isGuest && (
                  <span
                    className="cursor-pointer small text-unset"
                    onClick={() => setIsOpen(true)}
                  >
                    View Saved Addresses
                  </span>
                )}
                {isOpen && (
                  <span
                    className="cursor-pointer small text-unset"
                    onClick={() => setIsOpen(false)}
                  >
                    Use New Address
                  </span>
                )}
              </div>
              <div className={clsx("order_option_input", isOpen && "d-none")}>
                <APIProvider apiKey={API_KEY}>
                  <PlaceAutocomplete
                    onPlaceSelect={handlePlaceSelect}
                    defaultValue={shippingLocation?.formatted_address}
                    errors={errors}
                  />

                  {/* <GoogleMap selectedPlace={selectedPlace} /> */}

                  <div className="position-relative">
                    <span className="d-flex" style={{ color: "#231f2066" }}>
                      <HouseDoor />{" "}
                    </span>
                    <input
                      id="flatNumberAddress"
                      className={clsx(
                        "form-control",
                        errors?.address_street && "is-invalid",
                      )}
                      data-required="Please enter your House no., apt., suite, floor, street, etc."
                      placeholder="House no., apt., suite, floor, street, etc."
                      type="text"
                      maxLength={20}
                      defaultValue={shippingLocation?.address_street_1}
                      onInput={handleHouseAddressChange}
                    />
                    {errors?.address_street && (
                      <div className="invalid-feedback">
                        {errors?.address_street}
                      </div>
                    )}
                  </div>
                </APIProvider>
              </div>

              {isOpen && (
                <SavedAddressOptions handleChange={handleAddressChange} />
              )}
            </div>
          )}

          {selectedType === OrderType.DINEIN && (
            <div className="order_option_popup_sec2">
              <h3>Table Number</h3>
              <div className={clsx("order_option_input", "position-relative")}>
                <span className="d-flex" style={{ color: "#231f2066" }}>
                  <BsTable />{" "}
                </span>
                <input
                  id="tableNumber"
                  className={clsx(
                    "form-control",
                    errors?.table_number && "is-invalid",
                  )}
                  placeholder="Enter table number"
                  type="text"
                  maxLength={3}
                  // defaultValue={tableNumber}
                  value={tableNumber}
                  onInput={handleTableNumberChange}
                />
                {errors?.table_number && (
                  <div className="invalid-feedback">{errors?.table_number}</div>
                )}
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer className="text-center app-modal-footer">
          <button
            type="button"
            onClick={handleSubmit}
            disabled={isLoading}
            className="secondary-button"
          >
            {isLoading ? (
              <Spinner
                as="span"
                animation="border"
                size="sm"
                role="status"
                aria-hidden="true"
              />
            ) : selectedType === OrderType.PICKUP ? (
              "Confirm Pickup"
            ) : selectedType === OrderType.DELIVERY ? (
              "Confirm Delivery"
            ) : (
              "Confirm Dine In"
            )}
          </button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default OrderOptionModal;
